/*
 This file is part of GNU Taler
 (C) 2022-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  AccessToken,
  HttpStatusCode,
  OperationFail,
  Result,
  TalerCorebankApi,
  TalerCoreBankResultByMethod,
  TalerError,
  TalerHttpError,
} from "@gnu-taler/taler-util";
import { useEffect } from "preact/hooks";
import { useSessionState } from "./session.js";

import {
  LONG_POLL_DELAY,
  PaginatedList,
  buildPaginatedResult,
  useBankCoreApiContext,
  useListPointer,
  useLongPolling,
} from "@gnu-taler/web-util/browser";
import _useSWR, { mutate, SWRHook } from "swr";
import { PAGINATED_LIST_REQUEST } from "../utils.js";
const useSWR = _useSWR as unknown as SWRHook;

export function revalidateAccountDetails() {
  return mutate(
    (key) => Array.isArray(key) && key[key.length - 1] === "getAccount",
    undefined,
    { revalidate: true },
  );
}

export function useAccountDetails(account: string) {
  const { state: credentials } = useSessionState();
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();

  async function fetcher([username, token]: [string, AccessToken]) {
    return await api.getAccount({ username, token });
  }
  const token =
    credentials.status !== "loggedIn" ? undefined : credentials.token;
  const { data, error } = useSWR<
    TalerCoreBankResultByMethod<"getAccount">,
    TalerHttpError
  >([account, token, "getAccount"], fetcher, {});

  if (data) return data;
  if (error) return error;
  return undefined;
}

export function revalidateWithdrawalDetails() {
  return mutate(
    (key) => Array.isArray(key) && key[key.length - 1] === "getWithdrawalById",
    undefined,
    { revalidate: true },
  );
}

export function useWithdrawalDetails(wid: string | undefined) {
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();

  type Res = undefined | Awaited<ReturnType<typeof api.getWithdrawalById>>;
  const result = useLongPolling(
    (ct, res): Promise<Res> | undefined => {
      if (!wid) return undefined;
      const old_state =
        !res || res instanceof TalerError || res.type === "fail"
          ? undefined
          : res.body.status;

      return api.getWithdrawalById(wid, {
        old_state,
        timeoutMs: !old_state ? undefined : LONG_POLL_DELAY,
        ct,
      });
    },
    {
      deps: [wid],
      shouldStop(res) {
        return (
          res?.type === "ok" &&
          (res.body.status === "confirmed" || res.body.status === "aborted")
        );
      },
    },
  );

  return result;
}

export async function revalidatePublicAccounts() {
  return mutate(
    (key) => Array.isArray(key) && key[key.length - 1] === "getPublicAccounts",
    undefined,
    { revalidate: true },
  );
}
export function usePublicAccounts(
  filterAccount: string | undefined,
  initial?: number,
) {
  const [pointer, setPointer] = useListPointer(
    (row: TalerCorebankApi.PublicAccount) => String(row.row_id ?? 0),
    filterAccount,
    {
      id: initial === undefined ? undefined : String(initial),
      displayOrder: "asc",
    },
  );

  const {
    lib: { bank: api },
  } = useBankCoreApiContext();

  async function fetcher([account, txid, order]: [
    string | undefined,
    string | undefined,
    "asc" | "dec",
  ]) {
    return await api.getPublicAccounts(
      { account },
      {
        limit: PAGINATED_LIST_REQUEST,
        offset: txid,
        order,
      },
    );
  }

  const { data, error } = useSWR<
    TalerCoreBankResultByMethod<"getPublicAccounts">,
    TalerHttpError
  >([filterAccount, pointer.id, pointer.order, "getPublicAccounts"], fetcher, {
    refreshInterval: 0,
    refreshWhenHidden: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshWhenOffline: false,
    errorRetryCount: 0,
    errorRetryInterval: 1,
    shouldRetryOnError: false,
    keepPreviousData: true,
  });

  if (error) return Result.error(error);
  if (data === undefined) return undefined;
  if (data.type !== "ok") return Result.error(data);

  return Result.of(
    buildPaginatedResult(
      data.body.public_accounts,
      pointer,
      setPointer,
      PAGINATED_LIST_REQUEST,
    ),
  );
}

export function revalidateTransactions() {
  return mutate(
    (key) => Array.isArray(key) && key[key.length - 1] === "getTransactions",
    undefined,
    { revalidate: true },
  );
}

function transactionRowId(
  transaction: TalerCorebankApi.BankAccountTransactionInfo,
): string {
  return String(transaction.row_id);
}

export function useTransactions(
  account: string,
  anonymous = false,
  pageSize = 20,
):
  | Result<
      PaginatedList<TalerCorebankApi.BankAccountTransactionInfo>,
      | TalerError<{
          requestUrl: string;
          requestMethod: string;
        }>
      | OperationFail<HttpStatusCode.NotFound>
      | OperationFail<HttpStatusCode.Unauthorized>
    >
  | undefined {
  const { state: credentials } = useSessionState();
  const token =
    anonymous || credentials.status !== "loggedIn"
      ? undefined
      : credentials.token;

  const [pointer, setPointer] =
    useListPointer<TalerCorebankApi.BankAccountTransactionInfo>(
      transactionRowId,
    );

  useEffect(
    () => setPointer(undefined, "dec"),
    [account, anonymous, pageSize, setPointer],
  );
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();

  async function fetcher([username, token, offset, order]: [
    string,
    AccessToken | undefined,
    string | undefined,
    "asc" | "dec",
  ]) {
    const params = {
      limit: pageSize + 1,
      offset,
      order,
    };
    return token === undefined
      ? api.getPublicTransactions(username, params)
      : api.getTransactions({ username, token }, params);
  }

  const { data, error } = useSWR<
    Awaited<ReturnType<typeof api.getTransactions>>,
    TalerHttpError
  >(
    anonymous || token !== undefined
      ? [account, token, pointer.id, pointer.order, pageSize, "getTransactions"]
      : null,
    fetcher,
    {
      refreshInterval: 10000,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      // revalidateOnMount: false,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: true,
    },
  );
  if (error) return Result.error(error);
  if (data === undefined) return undefined;
  if (data.type !== "ok") return Result.error(data);

  return Result.of(
    buildPaginatedResult(
      data.body.transactions,
      pointer,
      setPointer,
      pageSize + 1,
    ),
  );
}
